import { useQuery, useMutation, useQueryClient } from 'react-query';
import ApiService from '@/services/api';
import { IWorkShiftCreateRequest, IWorkShiftListParams, IEmployeeListItem } from '@/types/workshift';

// ============================================================================
// Query Keys
// ============================================================================

export const WORKSHIFT_QUERY_KEYS = {
  all: ['workshifts'] as const,
  lists: () => [...WORKSHIFT_QUERY_KEYS.all, 'list'] as const,
  list: (params: IWorkShiftListParams) => [...WORKSHIFT_QUERY_KEYS.lists(), params] as const,
  summary: (params: IWorkShiftListParams) => [...WORKSHIFT_QUERY_KEYS.all, 'summary', params] as const,
  salarySlip: (params: IWorkShiftListParams) => [...WORKSHIFT_QUERY_KEYS.all, 'salary-slip', params] as const,
};

// ============================================================================
// Work Shift Hooks
// ============================================================================

export const useWorkShifts = (params: IWorkShiftListParams & { user?: string }) => {
  return useQuery(
    [...WORKSHIFT_QUERY_KEYS.list(params), params.user],
    () => ApiService.getWorkShifts(params),
    {
      keepPreviousData: true,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const useCreateWorkShift = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: IWorkShiftCreateRequest) => ApiService.createWorkShift(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(WORKSHIFT_QUERY_KEYS.lists());
        queryClient.invalidateQueries(['workshifts', 'summary']);
        queryClient.invalidateQueries(['workshifts', 'salary-slip']);
      },
    }
  );
};

export const useUpdateWorkShift = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ date, data }: { date: string; data: IWorkShiftCreateRequest }) =>
      ApiService.updateWorkShift(date, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(WORKSHIFT_QUERY_KEYS.lists());
        queryClient.invalidateQueries(['workshifts', 'summary']);
        queryClient.invalidateQueries(['workshifts', 'salary-slip']);
      },
    }
  );
};

export const useDeleteWorkShift = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (date: string) => ApiService.deleteWorkShift(date),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(WORKSHIFT_QUERY_KEYS.lists());
        queryClient.invalidateQueries(['workshifts', 'summary']);
        queryClient.invalidateQueries(['workshifts', 'salary-slip']);
      },
    }
  );
};

// ============================================================================
// Summary & Salary Slip Hooks
// ============================================================================

export const useMonthlySummary = (params: IWorkShiftListParams & { user?: string }) => {
  return useQuery(
    [...WORKSHIFT_QUERY_KEYS.summary(params), params.user],
    () => ApiService.getMonthlySummary(params),
    {
      staleTime: 60000,
      cacheTime: 300000,
    }
  );
};

export const useSalarySlip = (params: IWorkShiftListParams & { user?: string }) => {
  return useQuery(
    [...WORKSHIFT_QUERY_KEYS.salarySlip(params), params.user],
    () => ApiService.getSalarySlip(params),
    {
      staleTime: 60000,
      cacheTime: 300000,
    }
  );
};

// ============================================================================
// Employee & Notes Hooks
// ============================================================================

export const useEmployees = () => {
  return useQuery<IEmployeeListItem[], Error>(
    ['employees'],
    () => ApiService.getEmployees(),
    {
      staleTime: 300000,
      cacheTime: 600000,
      retry: false,
    }
  );
};

export const useUpdateWorkShiftNotes = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ user, date, notes }: { user: string; date: string; notes: string }) =>
      ApiService.updateWorkShiftNotes(user, date, notes),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['workshifts', 'salary-slip']);
      },
    }
  );
};
